/**
 * Payroll export and ROE inputs (sprint 2026-10-04 WP9, D93/D94). Mirrors the
 * backend `payroll-export` module (`GET companies/:companyId/payroll-export`
 * and `.../payroll-export/download`) and the ROE fields on terminations.
 */
import {
  TerminationReasonCategory,
  type DepartureType,
} from './workforce-analytics';

/**
 * `csv` / `xlsx`: the Summary (WP9 wide row); `review_xlsx`: Summary, Lines,
 * Changes, Warnings; `generic_csv`: the Lines with stable English headers;
 * `provider`: the provider's file (blocked until codes and numbers are set).
 */
export type PayrollExportFormat =
  'csv' | 'xlsx' | 'review_xlsx' | 'generic_csv' | 'provider';

/** Longest period one export may cover, in days (inclusive). */
export const PAYROLL_EXPORT_MAX_DAYS = 366;

/**
 * Service Canada Record of Employment reason for issuing (block 16). Mirror
 * of the backend `RoeReasonCode`. Names live in the `employee` namespace
 * under `roe.reasons.<code>`.
 */
export enum RoeReasonCode {
  A = 'A',
  B = 'B',
  D = 'D',
  E = 'E',
  F = 'F',
  G = 'G',
  H = 'H',
  J = 'J',
  K = 'K',
  M = 'M',
  N = 'N',
  P = 'P',
  Z = 'Z',
}

export const ROE_REASON_CODES: readonly RoeReasonCode[] =
  Object.values(RoeReasonCode);

/** ROE expected date of recall (block 14). */
export enum ExpectedRecall {
  Unknown = 'unknown',
  NotReturning = 'not_returning',
  Date = 'date',
}

/**
 * The ROE code the terminate dialog proposes from the departure type and
 * reason (HR can change it). Absent when no single code fits, e.g. an
 * involuntary departure with no reason (dismissal M or shortage of work A).
 */
export const SUGGESTED_ROE_CODE: Record<
  DepartureType,
  Partial<Record<TerminationReasonCategory | 'none', RoeReasonCode>>
> = {
  voluntary: {
    none: RoeReasonCode.E,
    [TerminationReasonCategory.CareerOpportunity]: RoeReasonCode.E,
    [TerminationReasonCategory.Compensation]: RoeReasonCode.E,
    [TerminationReasonCategory.Relocation]: RoeReasonCode.E,
    [TerminationReasonCategory.Personal]: RoeReasonCode.E,
    [TerminationReasonCategory.WorkEnvironment]: RoeReasonCode.E,
    [TerminationReasonCategory.Other]: RoeReasonCode.E,
    [TerminationReasonCategory.Retirement]: RoeReasonCode.G,
  },
  involuntary: {
    [TerminationReasonCategory.Performance]: RoeReasonCode.M,
    [TerminationReasonCategory.Conduct]: RoeReasonCode.M,
    [TerminationReasonCategory.Restructuring]: RoeReasonCode.A,
    [TerminationReasonCategory.Layoff]: RoeReasonCode.A,
    [TerminationReasonCategory.EndOfContract]: RoeReasonCode.A,
  },
};

/** The ROE inputs as a termination carries them (null on history). */
export interface TerminationRoeInputs {
  roeReasonCode: RoeReasonCode | null;
  /** `YYYY-MM-DD` */
  lastDayWorked: string | null;
  expectedRecall: ExpectedRecall | null;
  /** `YYYY-MM-DD`, only when `expectedRecall` is `date`. */
  expectedRecallDate: string | null;
  /** ROE block 17 (shown with employee_salary.read) and block 18. */
  payInLieuAmount?: string | null;
  severanceAmount?: string | null;
  roeComment?: string | null;
}

export interface PayrollExportQuery {
  /** `YYYY-MM-DD`, inclusive */
  from: string;
  to: string;
  branchId?: string;
}

export interface PayrollExportColumn {
  /** Fixed ids, or `leave:<leaveTypeId>` for one leave type. */
  id: string;
  /** In the request language. */
  label: string;
  kind: 'text' | 'date' | 'number' | 'boolean';
}

export type PayrollExportCell = string | number | boolean | null;

/** `GET companies/:companyId/payroll-export`: the file's rows, on screen. */
export interface PayrollExportPreview {
  from: string;
  to: string;
  columns: PayrollExportColumn[];
  rows: PayrollExportCell[][];
  /** False without `employee_salary.read`: salary cells are blank. */
  salaryVisible: boolean;
  summary: {
    employees: number;
    withPendingHours: number;
    terminationsMissingRoe: number;
    /** Employees with weekly overtime in the period (PAY-D5). */
    withOvertime: number;
  };
  /** Weekly overtime rule applied: minutes and first weekday (0 = Sunday). */
  overtime: { thresholdMin: number; weekStart: number };
  /** The period's last export and who changed since (spec 2.5). */
  state: PayrollPeriodState;
  /** The provider in Payroll settings. */
  provider: PayrollProvider | string;
  /** The provider file's layout (Beta until a real import passes). */
  providerFile: { fileFormat: string; ext: 'csv' | 'xlsx'; beta: boolean };
  /** What the provider file holds (Lines tab). */
  lines: PayrollLine[];
  /** Hires, departures, pay changes (Changes tab). */
  changes: PayrollChange[];
  /** Everything to check before sending (Warnings tab). */
  warnings: PayrollWarning[];
  /** Why the provider file cannot download yet (PAY-D7). */
  blocks: PayrollBlocks;
}

/* ---------- PR 3: codes, numbers, lines ---------- */

export enum PayrollItemKind {
  Regular = 'regular',
  Overtime = 'overtime',
  HolidayWorked = 'holiday_worked',
  StatHoliday = 'stat_holiday',
  LeaveType = 'leave_type',
  BonusCategory = 'bonus_category',
  BonusType = 'bonus_type',
  PayInLieu = 'pay_in_lieu',
  Severance = 'severance',
  VacationPayout = 'vacation_payout',
}

/** One line of the provider file (hours or an amount, never both). */
export interface PayrollLine {
  employeeId: string;
  employeeNumber: string | null;
  lastName: string | null;
  firstName: string | null;
  location: string | null;
  itemKind: PayrollItemKind;
  itemRef: string;
  kollabCode: string;
  name: string;
  /** In the request language. */
  label: string;
  providerCode: string | null;
  hours: number | null;
  amount: number | null;
  rate: number | null;
  dateFrom: string;
  dateTo: string;
  absenceFrom: string | null;
  absenceTo: string | null;
  isPaid: boolean | null;
  isVacation: boolean;
  /** A percentage bonus valued from the base pay (PAY-D14). */
  computed?: boolean;
}

/** What payroll needs to set up a new hire. */
export interface PayrollHireDetails {
  dateOfBirth: string | null;
  workEmail: string | null;
  department: string | null;
  jobTitle: string | null;
  employmentType: string | null;
  address: string | null;
}

export interface PayrollChange {
  type: 'hire' | 'termination' | 'pay_change';
  employeeId: string;
  employeeNumber: string | null;
  lastName: string | null;
  firstName: string | null;
  date: string;
  roeReasonCode?: string | null;
  lastDayWorked?: string | null;
  expectedRecall?: ExpectedRecall | null;
  expectedRecallDate?: string | null;
  newRate?: number | null;
  previousRate?: number | null;
  payType?: string | null;
  /** Salary change reason code (merit, promotion...). */
  reason?: string | null;
  location?: string | null;
  hire?: PayrollHireDetails | null;
  vacationPercent?: number | null;
  /** Never in the preview: only in the review workbook when asked. */
  sin?: string | null;
  payInLieuAmount?: number | null;
  severanceAmount?: number | null;
  roeComment?: string | null;
  vacationPayoutHours?: number | null;
}

/** Already translated into the request language. */
export interface PayrollWarning {
  type: string;
  message: string;
}

export interface PayrollBlocks {
  unmappedLeaveTypes: { id: string; name: string }[];
  unmappedItems: { kind: PayrollItemKind; ref: string; name: string }[];
  missingNumbers: { id: string; name: string }[];
  duplicateNumbers: { number: string; names: string[] }[];
}

/** One Codes tab row. */
export interface PayrollCodeItem {
  kind: PayrollItemKind;
  ref: string;
  name: string;
  defaultCode: string | null;
  providerCode: string | null;
  doNotExport: boolean;
  isPaid: boolean | null;
  isVacation: boolean;
  saved: boolean;
  needsCode: boolean;
}

/** `GET companies/:companyId/payroll-export/codes`. */
export interface PayrollCodes {
  provider: PayrollProvider | string;
  items: PayrollCodeItem[];
  providerCodes: { code: string; label: string }[];
}

export interface UpdatePayrollCodeItem {
  kind: PayrollItemKind;
  ref?: string;
  providerCode?: string | null;
  doNotExport?: boolean;
  isPaid?: boolean | null;
  isVacation?: boolean;
}

/** `GET companies/:companyId/payroll-export/employees`. */
export interface PayrollEmployeeNumber {
  employeeId: string;
  name: string;
  referenceId: string | null;
  location: string | null;
  payrollNumber: string | null;
  source: 'link' | 'reference' | 'none';
}

/* ---------- Payroll integration v1 (spec 2026-10-13), PR 2 ---------- */

/** Mirror of the backend `PayrollProvider`; `generic` = Other. */
export enum PayrollProvider {
  Generic = 'generic',
  Nethris = 'nethris',
  EmployeurD = 'employeur_d',
  AdpWfn = 'adp_wfn',
  AdpTeampay = 'adp_teampay',
  Powerpay = 'powerpay',
  Dayforce = 'dayforce',
  Wagepoint = 'wagepoint',
  Qbo = 'qbo',
  Humi = 'humi',
  Maestro = 'maestro',
}

/** The providers offered in Payroll settings, in display order. */
export const PAYROLL_PROVIDER_OPTIONS: readonly PayrollProvider[] = [
  PayrollProvider.Nethris,
  PayrollProvider.EmployeurD,
  PayrollProvider.AdpWfn,
  PayrollProvider.AdpTeampay,
  PayrollProvider.Powerpay,
  PayrollProvider.Wagepoint,
  PayrollProvider.Qbo,
  PayrollProvider.Humi,
  PayrollProvider.Maestro,
  PayrollProvider.Generic,
];

/** Shipped "Beta" until a real import passes (PAY-D19). */
export const BETA_PROVIDERS: readonly PayrollProvider[] = [
  PayrollProvider.Nethris,
  PayrollProvider.EmployeurD,
  PayrollProvider.AdpWfn,
];

export enum PayFrequency {
  Weekly = 'weekly',
  BiWeekly = 'bi_weekly',
  SemiMonthly = 'semi_monthly',
  Monthly = 'monthly',
}

export const PAY_FREQUENCIES: readonly PayFrequency[] =
  Object.values(PayFrequency);

/** Weekly and bi-weekly schedules repeat from an anchor date. */
export const ANCHORED_FREQUENCIES: readonly PayFrequency[] = [
  PayFrequency.Weekly,
  PayFrequency.BiWeekly,
];

export enum VacationBasis {
  Earnings = 'earnings',
  HoursWorked = 'hours_worked',
}

/** Default weekly overtime threshold: 40 h (Quebec LNT s.52). */
export const DEFAULT_OVERTIME_THRESHOLD_MIN = 2400;

/** `GET/PUT companies/:companyId/payroll-export/settings`. */
export interface PayrollSettings {
  /** False until saved once (defaults shown). */
  configured: boolean;
  provider: PayrollProvider;
  payFrequency: PayFrequency | null;
  /** `YYYY-MM-DD`, first day of a past period (weekly / bi-weekly). */
  anchorPeriodStart: string | null;
  providerCompanyCode: string | null;
  providerExtra: { branchNo?: string; payrollNumber?: string };
  overtimeWeeklyThresholdMin: number;
  /** First weekday of an overtime week (0 = Sunday); null = Monday. */
  overtimeWeekStart: number | null;
  vacationBasis: VacationBasis;
  fileFormat: string;
  updatedAt: string | null;
}

export type UpdatePayrollSettingsPayload = Partial<
  Omit<PayrollSettings, 'configured' | 'fileFormat' | 'updatedAt'>
>;

export interface PayrollRunPerson {
  id: string;
  name: string;
}

/** Aggregate totals stored on a run (never per person). */
export interface PayrollRunTotals {
  regularHours?: number;
  overtimeHours?: number;
  holidayWorkedHours?: number;
  statHolidayHours?: number;
  leaveHours?: number;
  pendingHours?: number;
}

/** One download (`payroll_export_runs`); the file itself is never stored. */
export interface PayrollRunSummary {
  id: string;
  periodStart: string;
  periodEnd: string;
  provider: PayrollProvider | string;
  fileFormat: string;
  branchIds: string[];
  employeeCount: number;
  lineCount: number;
  totals: PayrollRunTotals;
  salaryIncluded: boolean;
  sinIncluded: boolean;
  sinCount: number;
  supersedesRunId: string | null;
  createdAt: string;
  createdBy: PayrollRunPerson;
  finalizedAt: string | null;
  finalizedBy: PayrollRunPerson | null;
}

export type PayrollPeriodStatus = 'not_exported' | 'exported' | 'changed';

export interface PayrollPeriodState {
  start: string;
  end: string;
  status: PayrollPeriodStatus;
  lastRun: PayrollRunSummary | null;
  changedEmployees: PayrollRunPerson[];
}

/** `GET companies/:companyId/payroll-export/periods`. */
export interface PayrollPeriods {
  settings: PayrollSettings;
  /** Today in the company timezone. */
  today: string;
  /** Null without a complete pay schedule (Custom range). */
  current: { start: string; end: string } | null;
  /** Next, current and 12 previous, newest first. */
  periods: (PayrollPeriodState & { isCurrent: boolean })[];
}

/** Activity event written for each download (module `PAYROLL`, no target). */
export const PAYROLL_EXPORTED_EVENT = 'PAYROLL_EXPORTED';
/** A run marked as sent to payroll. */
export const PAYROLL_RUN_FINALIZED_EVENT = 'PAYROLL_RUN_FINALIZED';
/** Payroll settings saved. */
export const PAYROLL_SETTINGS_UPDATED_EVENT = 'PAYROLL_SETTINGS_UPDATED';
/** Payroll codes saved. */
export const PAYROLL_CODES_UPDATED_EVENT = 'PAYROLL_CODES_UPDATED';
/** An employee's payroll number changed. */
export const PAYROLL_EMPLOYEE_NUMBER_UPDATED_EVENT =
  'PAYROLL_EMPLOYEE_NUMBER_UPDATED';

/** Metadata of the `PAYROLL_EXPORTED` activity entry. */
export interface PayrollExportedMetadata {
  actor: { id: string; name: string };
  /** Since payroll v1 PR 2. */
  runId?: string;
  rowCount: number;
  /** `xlsx` / `csv` (WP9) or the run format, e.g. `summary_xlsx`. */
  format: string;
  from: string;
  to: string;
  branchIds: string[];
  salaryIncluded: boolean;
}

/** Metadata of `PAYROLL_RUN_FINALIZED`. */
export interface PayrollRunFinalizedMetadata {
  actor: { id: string; name: string };
  runId: string;
  from: string;
  to: string;
}

/** Metadata of `PAYROLL_SETTINGS_UPDATED` (field names, never values). */
export interface PayrollSettingsUpdatedMetadata {
  actor: { id: string; name: string };
  changedFields: string[];
  provider: { before: string; after: string };
  payFrequency: { before: string | null; after: string | null };
}

/** Metadata of `PAYROLL_CODES_UPDATED`. */
export interface PayrollCodesUpdatedMetadata {
  actor: { id: string; name: string };
  provider: string;
  count: number;
}

/** Metadata of `PAYROLL_EMPLOYEE_NUMBER_UPDATED`. */
export interface PayrollEmployeeNumberUpdatedMetadata {
  actor: { id: string; name: string };
  employee: { id: string; name: string };
  provider: string;
  before: string | null;
  after: string | null;
}
